import {
  Button,
  CardTitle,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  UncontrolledDropdown
} from 'reactstrap';
import CardInner from '../../../../components/cards/CardInner';
import CardTitleGroup from '../../../../components/cards/CardTitleGroup';
import CardTools from '../../../../components/cards/CardTools';
import { Icon, PaginationComponent, TooltipComponent } from '../../../../components/Component';
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import TransactionsSkeletonLoader from './components/TransactionsSkeletonLoader';
import dealQueries, { DEAL_KEYS } from '../../../../queries/dealQueries';
import useAxiosPrivate from '../../../../hooks/useAxiosPrivate';
import { useMutation, useQueryClient } from 'react-query';
import { showToast } from '../../../../utils/toast/toast';
import LoaderModal from '../../../../components/modals/LoaderModal';
import { truncate } from 'lodash';

// Import the custom transaction modal
const PayableTransactionModal = lazy(() => import('./components/PayableTransactionModal'));
const ConfirmationModal = lazy(() => import('../../../../components/modals/ConfirmationModal'));
const ProjectedFieldsModal = lazy(() => import('./components/ProjectedFieldsModal'));

// Constants specific to Payables Table
const PAYABLES_HEADER_FIELDS = ['Paid To', 'Type', 'Amount', 'Payment Date', 'Note'];

const PAYABLES_BREAKPOINT_HEADERS = {
  'Payment Date': true,
  Note: true
};

const PER_PAGE_OPTIONS = [5, 10, 20, 50];

const PayablesTableView = ({
  title,
  transactionsState,
  deal,
  queryParams,
  setQueryParams,
  refetchFn,
  isModalOpen,
  toggleModal,
  modalId,
  remainingPayableAmount
}) => {
  const [searchText, setSearchText] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const toggleSearch = () => setIsSearchOpen(!isSearchOpen);
  const searchInputRef = useRef(null);

  // Transaction being edited
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  useEffect(() => {
    if (isSearchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchOpen]);

  const handleEditTransaction = (transaction) => {
    setEditingTransaction(transaction);
    setIsEditMode(true);
    toggleModal();
  };

  const handleAddTransaction = () => {
    setEditingTransaction(null);
    setIsEditMode(false);
    toggleModal();
  };

  return (
    <>
      <CardInner>
        <CardTitleGroup>
          <CardTitle>
            <h5 className="title">{title}</h5>
          </CardTitle>
          <CardTools>
            <div className="form-inline flex-nowrap gx-3"></div>
          </CardTools>
          <CardTools className="me-n1">
            <ul className="btn-toolbar gx-1">
              <li>
                <a
                  href="#search"
                  onClick={(ev) => {
                    ev.preventDefault();
                    toggleSearch();
                  }}
                  className="btn btn-icon btn-trigger search-toggle toggle-search"
                >
                  <Icon name="search"></Icon>
                </a>
              </li>

              <li className="btn-toolbar-sep"></li>

              <li onClick={handleAddTransaction}>
                <TooltipComponent
                  tag="a"
                  containerClassName="btn btn-trigger btn-icon"
                  id={`add-payable-for-${deal?.id}`}
                  icon="plus"
                  direction="top"
                  text="Add Payable"
                />
              </li>

              <li>
                <div className="toggle-wrap">
                  <div>
                    <ul className="btn-toolbar gx-1">
                      <li>
                        <UncontrolledDropdown>
                          <DropdownToggle tag="span" className="btn btn-trigger btn-icon dropdown-toggle">
                            <Icon name="setting"></Icon>
                          </DropdownToggle>
                          <DropdownMenu end className="dropdown-menu-xs">
                            <ul className="link-check">
                              <li>
                                <span>Show</span>
                              </li>
                              {PER_PAGE_OPTIONS.map((option, idx) => (
                                <li key={`pay-option-${idx}`}>
                                  <DropdownItem
                                    tag="span"
                                    className={queryParams?.pageSize === option ? 'active' : ''}
                                    onClick={(ev) => {
                                      ev.preventDefault();
                                      setQueryParams((prev) => ({
                                        ...prev,
                                        pageSize: option,
                                        page: 1
                                      }));
                                    }}
                                  >
                                    {option}
                                  </DropdownItem>
                                </li>
                              ))}
                            </ul>
                          </DropdownMenu>
                        </UncontrolledDropdown>
                      </li>
                    </ul>
                  </div>
                </div>
              </li>
            </ul>
          </CardTools>
          <div className={`card-search search-wrap ${isSearchOpen && 'active'}`}>
            <div className="card-body p-0">
              <div className="search-content border rounded-lg">
                <Button
                  className="search-back btn-icon toggle-search active bg-transparent text-secondary ms-2"
                  onClick={() => {
                    setQueryParams((prev) => ({
                      ...prev,
                      payee: ''
                    }));
                    setSearchText('');
                    toggleSearch();
                  }}
                >
                  <Icon name="arrow-left"></Icon>
                </Button>
                <input
                  type="text"
                  className="border-transparent form-focus-none form-control ps-5 bg-transparent text-secondary"
                  placeholder="Search Payable Transactions by payee name"
                  value={searchText}
                  ref={searchInputRef}
                  onChange={(e) => {
                    setQueryParams((prev) => ({
                      ...prev,
                      payee: e.target.value,
                      page: 1
                    }));
                    setSearchText(e.target.value);
                  }}
                />
                <Button className="search-submit btn-icon bg-transparent text-secondary border-0 me-3">
                  <Icon name="search"></Icon>
                </Button>
              </div>
            </div>
          </div>
        </CardTitleGroup>
      </CardInner>
      <CardInner className="">
        <table className="table table-tranx border">
          <thead>
            <tr className="tb-tnx-head d-none d-md-table-row">
              {PAYABLES_HEADER_FIELDS.map((header, idx) => (
                <th
                  className={PAYABLES_BREAKPOINT_HEADERS[header] ? 'd-none d-md-table-cell text-center' : 'text-center'}
                  key={`payable-header-${idx}-${header}`}
                >
                  <span>{header}</span>
                </th>
              ))}
              <th>
                <span>&nbsp;</span>
              </th>
            </tr>
          </thead>
          <tbody className="">
            {transactionsState?.isLoading ? (
              <TransactionsSkeletonLoader />
            ) : (
              transactionsState?.transactions?.map((transaction, idx) => (
                <PayableTransactionItem
                  isLatest={idx === 0}
                  key={`payable-transaction-${idx}`}
                  transaction={transaction}
                  deal={deal}
                  refetchFn={refetchFn}
                  onEdit={handleEditTransaction}
                />
              ))
            )}
          </tbody>
        </table>
      </CardInner>
      <CardInner>
        {transactionsState?.error ? (
          <p className="text-center text-danger fs-6">
            Failed to fetch payable transactions.
            <br />
            {transactionsState?.error?.response?.data?.message ?? null}
          </p>
        ) : null}
        {transactionsState?.transactions?.length ? (
          <>
            <PaginationComponent
              itemPerPage={queryParams?.pageSize}
              totalItems={transactionsState?.totalTransactions}
              paginate={(page) => setQueryParams((prev) => ({ ...prev, page }))}
              currentPage={queryParams?.page}
            />
          </>
        ) : (
          !transactionsState?.isLoading && (
            <p className="text-center fw-bold fs-6">
              No Payable Transactions found.
              <br />
            </p>
          )
        )}
      </CardInner>

      {/* Payable Transaction Modal */}
      <Suspense fallback={<LoaderModal />}>
        {isModalOpen && (
          <PayableTransactionModal
            isOpen={isModalOpen}
            toggleFn={toggleModal}
            deal={deal}
            previousTransactions={transactionsState?.transactions ?? []}
            refetchFn={refetchFn}
            id={modalId}
            transaction={editingTransaction}
            isEditMode={isEditMode}
            remainingPayableAmount={remainingPayableAmount}
          />
        )}
      </Suspense>
    </>
  );
};

const PayableTransactionItem = ({ transaction, isLatest, deal, refetchFn, onEdit }) => {
  const axios = useAxiosPrivate();
  const queryClient = useQueryClient();
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const toggleDeleteModal = () => setIsDeleteModalOpen(!isDeleteModalOpen);
  
  // New state for ProjectedFieldsModal
  const [isProjectedFieldsModalOpen, setIsProjectedFieldsModalOpen] = useState(false);
  const toggleProjectedFieldsModal = () => setIsProjectedFieldsModalOpen(!isProjectedFieldsModalOpen);

  const toggleNoteModal = () => {
    if (!transaction?.note || transaction?.note?.length === 0) {
      return;
    }
    setIsNoteModalOpen(!isNoteModalOpen);
  };

  const transactionId = useMemo(() => transaction?.id ?? transaction?._id, [transaction]);

  const { mutationFn } = dealQueries.Transactions.delete(axios, transactionId);
  const deleteTransaction = useMutation(mutationFn, {
    onSettled: () => {
      queryClient.invalidateQueries(DEAL_KEYS.transactions.baseKey);
      toggleDeleteModal();
    },
    onSuccess: () => {
      showToast('Deleted payable transaction successfully.', 'success');
      refetchFn && typeof refetchFn === 'function' && refetchFn();
    },
    onError: (error) => {
      console.log(error?.response?.message, 'ERROR DATA');
      showToast(error.message, 'error');
    }
  });

  const handleDeleteTransaction = async () => {
    deleteTransaction.mutate();
  };

  const isVirtual = transaction?.amount === 0;
  const displayDate = !isVirtual
    ? format(new Date(transaction?.paymentDate), 'dd/MM/yyyy hh:mm a')
    : transaction?.projectedDate
    ? `${format(new Date(transaction?.projectedDate), 'dd/MM/yyyy')} (PROJECTED)`
    : 'Unknown';
  const displayAmount = !isVirtual
    ? Number(transaction?.amount)?.toFixed(4)
    : transaction?.projectedAmount
    ? `${Number(transaction?.projectedAmount)?.toFixed(4)} (PROJECTED)`
    : 0;

  return (
    <>
      <tr className="tb-tnx-item bg-white">
        <td className="bg-white fw-bold text-center">{transaction?.payeeName ?? 'Unknown'}</td>
        <td className="bg-white text-center">{transaction?.transactionType ?? 'Agent'}</td>
        <td className="bg-white fw-bold text-center">{displayAmount}</td>
        <td className="bg-white d-none d-md-table-cell text-center" style={{ color: '#8094ae' }}>
          {displayDate}
        </td>
        <td className="bg-white text-wrap d-none d-md-table-cell text-center">
          {truncate(transaction?.note ?? 'No note.', { length: 25 })}
        </td>
        <td className="bg-white text-end">
          {isLatest ? (
            <UncontrolledDropdown>
              <DropdownToggle tag="span" className="btn btn-trigger btn-icon dropdown-toggle">
                <Icon name={'more-h'} />
              </DropdownToggle>
              <DropdownMenu end>
                <ul className="link-list-opt no-bdr">
                  {transaction?.note && transaction?.note?.length > 0 ? (
                    <li>
                      <DropdownItem tag="a" href="#dropdown" onClick={toggleNoteModal}>
                        <Icon name="question-alt"></Icon>
                        <span>View Note</span>
                      </DropdownItem>
                    </li>
                  ) : null}
                  <li>
                    <DropdownItem tag="a" className="cursor-pointer" onClick={() => onEdit(transaction)}>
                      <Icon name="edit"></Icon>
                      <span>Edit Transaction</span>
                    </DropdownItem>
                  </li>
                  {isVirtual && (
                    <li>
                      <DropdownItem tag="a" className="cursor-pointer" onClick={toggleProjectedFieldsModal}>
                        <Icon name="calendar-alt"></Icon>
                        <span>Edit Projected Date</span>
                      </DropdownItem>
                    </li>
                  )}
                  <li>
                    <DropdownItem tag="a" className="cursor-pointer" onClick={toggleDeleteModal}>
                      <Icon name="trash"></Icon>
                      <span>Delete Transaction</span>
                    </DropdownItem>
                  </li>
                </ul>
              </DropdownMenu>
            </UncontrolledDropdown>
          ) : (
            <span className={`btn btn-icon`} onClick={toggleNoteModal}>
              <TooltipComponent
                tag="a"
                containerClassName={`btn btn-trigger btn-icon text-primary ${
                  !transaction?.note || !transaction?.note?.length ? 'd-none' : ''
                }`}
                id={`view-note-for-payable-${transaction?.id ?? transaction?._id}`}
                icon="question-alt"
                direction="top"
                text={'View Note'}
              />
            </span>
          )}
        </td>
      </tr>
      {isNoteModalOpen && (
        <Suspense fallback={<LoaderModal />}>
          <Modal isOpen={isNoteModalOpen} toggle={toggleNoteModal}>
            <ModalHeader>
              <h5 className="title">Payable Transaction Note</h5>
            </ModalHeader>
            <ModalBody>
              <p>{transaction?.note}</p>
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" onClick={toggleNoteModal}>
                OK
              </Button>
            </ModalFooter>
          </Modal>
        </Suspense>
      )}
      {isDeleteModalOpen && (
        <Suspense fallback={<LoaderModal />}>
          <ConfirmationModal
            isOpen={isDeleteModalOpen}
            toggleFn={toggleDeleteModal}
            actionFn={handleDeleteTransaction}
            loadingState={deleteTransaction.status === 'loading'}
            title={'Delete Payable Transaction'}
            dimButtons
            isDeletion
          >
            <p>Are you sure you want to delete this payable transaction? This is irreversible.</p>
          </ConfirmationModal>
        </Suspense>
      )}
      {/* ProjectedFieldsModal */}
      {isProjectedFieldsModalOpen && (
        <Suspense fallback={<LoaderModal />}>
          <ProjectedFieldsModal
            isOpen={isProjectedFieldsModalOpen}
            toggleFn={toggleProjectedFieldsModal}
            transaction={transaction}
            deal={deal}
            refetchFn={refetchFn}
          />
        </Suspense>
      )}
    </>
  );
};

export default PayablesTableView;
